import { resetCollectionDescription } from '@theme/collection-description';
import { ThemeEvents } from '@theme/events';

/** @typedef {{ param_name: string, param_value: string, h1: string, title: string, description: string }} IndexableFilterEntry */

const SELECTORS = {
  indexableData: '#indexable-filters-data',
  collectionTitle: '[data-collection-title]',
  collectionDescription: '[data-collection-description]',
  originalDescription: '#original-collection-description',
  originalH1: 'meta[name="original-collection-h1"]',
  originalTitle: 'meta[name="original-title"]',
  breadcrumbCurrent: 'nav.breadcrumbs .breadcrumb-name',
};

/**
 * @returns {IndexableFilterEntry[]}
 */
function parseIndexableFilters() {
  const dataEl = document.querySelector(SELECTORS.indexableData);
  if (!(dataEl instanceof HTMLScriptElement) || !dataEl.textContent?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(dataEl.textContent);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {string} paramName
 * @returns {string}
 */
function getFilterGroupKey(paramName) {
  return paramName.replace(/\.(gte|lte)$/, '');
}

/**
 * @param {URLSearchParams} searchParams
 * @returns {Map<string, { param_name: string, value: string }[]>}
 */
function groupFilterParams(searchParams) {
  /** @type {Map<string, { param_name: string, value: string }[]>} */
  const groups = new Map();

  for (const [paramName, value] of searchParams.entries()) {
    if (!paramName.startsWith('filter.') || value === '') continue;

    const groupKey = getFilterGroupKey(paramName);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)?.push({ param_name: paramName, value });
  }

  return groups;
}

/**
 * @param {string} value
 * @returns {string}
 */
function decodeFilterParamValue(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Matches filter values 1:1 or after URL decoding (configured values are expected
 * URL-encoded as in the collection filter query string).
 *
 * @param {string} activeValue
 * @param {string} configuredValue
 * @returns {boolean}
 */
function paramValuesMatch(activeValue, configuredValue) {
  if (activeValue === configuredValue) return true;

  return (
    decodeFilterParamValue(activeValue) ===
    decodeFilterParamValue(configuredValue)
  );
}

/**
 * @param {URLSearchParams} searchParams
 * @param {IndexableFilterEntry[]} indexableFilters
 * @returns {IndexableFilterEntry | null}
 */
function resolveMatch(searchParams, indexableFilters) {
  if (indexableFilters.length === 0) return null;

  const groups = groupFilterParams(searchParams);
  const activeGroups = [...groups.values()].filter((group) => group.length > 0);

  const soleGroup = activeGroups[0];
  if (activeGroups.length !== 1 || !soleGroup || soleGroup.length !== 1) {
    return null;
  }

  const soleValue = soleGroup[0];
  if (!soleValue) return null;

  const { param_name, value } = soleValue;
  return (
    indexableFilters.find(
      (entry) =>
        entry.param_name === param_name &&
        paramValuesMatch(value, entry.param_value)
    ) ?? null
  );
}

/**
 * @returns {string}
 */
function getOriginalDescriptionComponentHtml() {
  const template = document.querySelector(SELECTORS.originalDescription);
  if (!(template instanceof HTMLTemplateElement)) return '';

  const component = template.content.querySelector('collection-description-component');
  return component instanceof HTMLElement ? component.outerHTML : '';
}

/**
 * @returns {string}
 */
function buildDefaultTitle() {
  const originalTitle = document.querySelector(SELECTORS.originalTitle);
  const pageTitle =
    originalTitle instanceof HTMLMetaElement ? originalTitle.content : '';
  if (!pageTitle) return document.title;

  return pageTitle;
}

/**
 * @param {string} title
 */
function setDocumentTitle(title) {
  if (document.title !== title) {
    document.title = title;
  }
}

/**
 * @param {string} h1
 */
function setCollectionTitle(h1) {
  const el = document.querySelector(SELECTORS.collectionTitle);
  if (el && el.textContent !== h1) {
    el.textContent = h1;
  }
}

/**
 * Updates the visible breadcrumb current-page label when present.
 * Breadcrumbs may be omitted from the template — no-op if missing.
 *
 * @param {string} text
 */
function setBreadcrumbCurrentPage(text) {
  const crumbs = document.querySelectorAll(SELECTORS.breadcrumbCurrent);
  if (crumbs.length === 0) return;

  const el = crumbs.item(crumbs.length - 1);
  if (el && el.textContent !== text) {
    el.textContent = text;
  }
}

/**
 * @param {string} html
 * @param {'content' | 'component'} [mode]
 */
function setCollectionDescription(html, mode = 'content') {
  const component = document.querySelector('collection-description-component');
  if (!component) return;

  if (mode === 'component') {
    if (html && component.outerHTML !== html) {
      component.outerHTML = html;
    }
    return;
  }

  const content = component.querySelector(SELECTORS.collectionDescription);
  if (content && content.innerHTML !== html) {
    content.innerHTML = html;
  }

  resetCollectionDescription();
}

function applyOriginal() {
  const originalH1 = document.querySelector(SELECTORS.originalH1);
  if (originalH1 instanceof HTMLMetaElement) {
    setCollectionTitle(originalH1.content);
    setBreadcrumbCurrentPage(originalH1.content);
  }

  setCollectionDescription(getOriginalDescriptionComponentHtml(), 'component');
  setDocumentTitle(buildDefaultTitle());
}

/**
 * @param {IndexableFilterEntry} entry
 */
function applyIndexable(entry) {
  if (entry.h1) {
    setCollectionTitle(entry.h1);
    setBreadcrumbCurrentPage(entry.h1);
  }

  const description = entry.description?.trim();
  if (description) {
    setCollectionDescription(description, 'content');
  } else {
    setCollectionDescription(getOriginalDescriptionComponentHtml(), 'component');
  }

  if (entry.title) {
    setDocumentTitle(`${entry.title} | BOSP`);
  }
}

/**
 * @param {URLSearchParams} searchParams
 */
function updateFromSearchParams(searchParams) {
  const indexableFilters = parseIndexableFilters();
  const match = resolveMatch(searchParams, indexableFilters);

  if (match) {
    applyIndexable(match);
  } else {
    applyOriginal();
  }
}

/**
 * @param {Event} event
 */
function handleFilterUpdate(event) {
  const queryParams =
    event instanceof CustomEvent &&
    event.detail?.queryParams instanceof URLSearchParams
      ? event.detail.queryParams
      : new URLSearchParams(location.search);

  updateFromSearchParams(queryParams);
}

document.addEventListener(ThemeEvents.FilterUpdate, handleFilterUpdate);
